import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { ICONS_WEB } from "@miplatform/constants";
import { getCurrencyString } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

const roundPrice = (price) => {
  return (Math.round((price + Number.EPSILON) * 100) / 100).toFixed(2);
};

/**
 * PriceRollup component displays a rollup of prices on a spec plan based on the provided data.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data object containing total and draws.
 * @param {number} props.data.total - The total price.
 * @param {Array} props.data.draws - An array of draw objects.
 * @param {string} props.data.draws[].draw - The title of the draw.
 * @param {number} props.data.draws[].value - The price value of the draw.
 *
 * @returns {JSX.Element} The rendered PriceRollup component.
 */
export function PriceRollup({
  data,
  optionDetailsMap = {},
  optionPriceSummaryMap = {},
}) {
  const { classes } = useStyles();
  const theme = useTheme();

  const { total, draws } = { ...data };

  const selectedOptions = Object.values(optionDetailsMap)
    .filter((option) => option.showPreview)
    .map(({ id }) => id);

  const selectedOptionsPrices = useMemo(
    () =>
      selectedOptions.reduce(
        (acc, optionId) => {
          const optionPrice = optionPriceSummaryMap[optionId] || {};

          if (optionPrice.draws?.length) {
            optionPrice.draws.forEach((draw) => {
              const existingDrawIndex = acc.draws.findIndex(
                (accDraw) => accDraw.draw === draw.draw
              );
              if (existingDrawIndex >= 0) {
                acc.draws[existingDrawIndex].value += draw.value;
              } else {
                acc.draws.push({ draw: draw.draw, value: draw.value });
              }
            });
          }

          acc.total += optionPrice.total || 0;
          return acc;
        },
        { draws: [], total: 0 }
      ),
    [selectedOptions.length, optionPriceSummaryMap]
  );

  const priceSection = (
    title,
    price,
    priceDelta = 0,
    backgroundColor = theme.palette.grey.light
  ) => {
    if (price === undefined) {
      return null;
    }

    const roundedPrice = roundPrice(price);
    const roundedPriceDelta = Math.abs(roundPrice(priceDelta));
    const roundedPriceTotal = roundPrice(price + priceDelta);

    return (
      <Box
        className={classes.priceContainer}
        sx={{ backgroundColor }}
        key={title}
      >
        <Box>
          <Typography variant="body1" className={classes.priceSectionTitle}>
            {title}:
          </Typography>
          <Typography
            variant="body1"
            className={classes.priceSectionPrice}
            sx={{ textDecoration: priceDelta !== 0 ? "line-through" : "none" }}
          >
            {getCurrencyString(roundedPrice)}
          </Typography>
        </Box>
        <Box>
          {roundedPriceDelta !== 0 && (
            <Box className={classes.columnEnd}>
              <Box display="flex" alignItems="end">
                <MiIcon
                  path={
                    priceDelta >= 0
                      ? mdi[ICONS_WEB.MENU_UP]
                      : mdi[ICONS_WEB.MENU_DOWN]
                  }
                  size={1}
                  color={
                    priceDelta >= 0
                      ? theme.palette.success.main
                      : theme.palette.error.main
                  }
                />
                <Typography variant="caption">
                  {getCurrencyString(roundedPriceDelta)}
                </Typography>
              </Box>
              <Typography variant="body1">
                {getCurrencyString(roundedPriceTotal)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={classes.priceRollupContainer}>
      {draws?.map((draw) => {
        const selectedOptionsDraw = selectedOptionsPrices?.draws?.find(
          (d) => d.draw === draw.draw
        );
        const priceDelta = selectedOptionsDraw ? selectedOptionsDraw.value : 0;
        return priceSection(draw.draw, draw.value, priceDelta);
      })}
      {priceSection(
        "Total",
        total,
        selectedOptionsPrices?.total,
        theme.palette.green.light
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  priceRollupContainer: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    flex: 2,
    gap: 8,
    [theme.breakpoints.up("md")]: {
      flex: 1,
    },
  },
  priceContainer: {
    display: "flex",
    minWidth: 90,
    gap: theme.spacing(4),
    maxHeight: 54,
    borderRadius: 8,
    padding: theme.spacing(1, 2),
  },
  columnStart: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  columnEnd: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  priceSectionTitle: {
    textTransform: "capitalize",
  },
}));
